import {
  API_ROUTES,
  interpolateRoute,
  type ApiResponse,
  type SaveWorkflowActionDto,
  type WorkflowActionCatalogEntry,
  type WorkflowActionUsage,
} from '@super/shared-types'
import { BaseApiClient } from './base-client'
import { withErrorHandling } from '../utils'

export type DeleteCatalogActionResult =
  | { ok: true }
  | { ok: false; code: 'error' }

/** Admin client for the v3 catalog of individual actions. Served by the API
 *  at /api/actions (customer) and /api/admin/actions (super-admin). The list
 *  endpoint surfaces both v3 standalone rows and v2 handoff-owned rows. */
export class ActionsCatalogApiClient extends BaseApiClient {
  async getActions(): Promise<WorkflowActionCatalogEntry[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry[]>
        >(API_ROUTES.ACTIONS.GET_ALL)
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching catalog actions',
      },
    )
    return result ?? []
  }

  async getActionsByIds(
    ids: readonly string[],
  ): Promise<WorkflowActionCatalogEntry[] | null> {
    if (ids.length === 0) return []
    // Return null on error so the caller can keep AsyncResource refs in the
    // `loading` state. `[]` means the server responded with an empty list,
    // which the caller then treats as "every requested id is missing".
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry[]>
        >(
          `${API_ROUTES.ACTIONS.GET_BY_IDS}?ids=${encodeURIComponent(ids.join(','))}`,
        )
        return response.data ?? []
      },
      {
        on404: { fallback: [] },
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error batch-fetching catalog actions',
      },
    )
  }

  async getActionById(id: string): Promise<WorkflowActionCatalogEntry | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry>
        >(interpolateRoute(API_ROUTES.ACTIONS.GET, { id }))
        return response.data ?? null
      },
      { on404: 'ignore', errorContext: 'Error fetching catalog action' },
    )
  }

  async getActionUsage(id: string): Promise<WorkflowActionUsage | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<WorkflowActionUsage>>(
          interpolateRoute(API_ROUTES.ACTIONS.GET_USAGE, { id }),
        )
        return response.data ?? null
      },
      { on404: 'ignore', errorContext: 'Error fetching catalog action usage' },
    )
  }

  async getActionUsageCounts(
    ids: readonly string[],
  ): Promise<Record<string, number>> {
    if (ids.length === 0) return {}
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<Record<string, number>>
        >(
          `${API_ROUTES.ACTIONS.GET_USAGE_COUNTS}?ids=${encodeURIComponent(ids.join(','))}`,
        )
        return response.data ?? {}
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching catalog action usage counts',
      },
    )
    return result ?? {}
  }

  async getActionUsageCountsForCompanyByAdmin(
    ids: readonly string[],
    companyId: string,
  ): Promise<Record<string, number>> {
    if (ids.length === 0) return {}
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<Record<string, number>>
        >(
          `${API_ROUTES.ADMIN.ACTIONS.GET_USAGE_COUNTS}?ids=${encodeURIComponent(ids.join(','))}&companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? {}
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching catalog action usage counts',
      },
    )
    return result ?? {}
  }

  async createAction(
    dto: SaveWorkflowActionDto,
  ): Promise<WorkflowActionCatalogEntry | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry>
        >(API_ROUTES.ACTIONS.CREATE, {
          method: 'POST',
          body: JSON.stringify(dto),
        })
        return response.data ?? null
      },
      { errorContext: 'Error creating catalog action' },
    )
  }

  async updateAction(
    id: string,
    dto: SaveWorkflowActionDto,
  ): Promise<WorkflowActionCatalogEntry | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry>
        >(interpolateRoute(API_ROUTES.ACTIONS.UPDATE, { id }), {
          method: 'PUT',
          body: JSON.stringify(dto),
        })
        return response.data ?? null
      },
      { errorContext: 'Error updating catalog action' },
    )
  }

  async bulkSave(payload: {
    existingId?: string
    actions: SaveWorkflowActionDto[]
  }): Promise<WorkflowActionCatalogEntry[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry[]>
        >(API_ROUTES.ACTIONS.BULK_SAVE, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
        return response.data ?? []
      },
      { errorContext: 'Error bulk-saving catalog actions' },
    )
    return result ?? []
  }

  async deleteAction(id: string): Promise<DeleteCatalogActionResult> {
    try {
      await this.request(interpolateRoute(API_ROUTES.ACTIONS.DELETE, { id }), {
        method: 'DELETE',
      })
      return { ok: true }
    } catch (error) {
      return parseDeleteError(error)
    }
  }

  // ─── Super-admin ────────────────────────────────────────────────────

  async getActionsForCompanyByAdmin(
    companyId: string,
  ): Promise<WorkflowActionCatalogEntry[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry[]>
        >(
          `${API_ROUTES.ADMIN.ACTIONS.GET_BY_COMPANY}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error fetching catalog actions for ${companyId}`,
      },
    )
    return result ?? []
  }

  async getActionsByIdsForCompany(
    companyId: string,
    ids: readonly string[],
  ): Promise<WorkflowActionCatalogEntry[] | null> {
    if (ids.length === 0) return []
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry[]>
        >(
          `${API_ROUTES.ADMIN.ACTIONS.GET_BY_IDS}?ids=${encodeURIComponent(ids.join(','))}&companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? []
      },
      {
        on404: { fallback: [] },
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error batch-fetching catalog actions for ${companyId}`,
      },
    )
  }

  async getActionForCompanyByAdmin(
    id: string,
    companyId: string,
  ): Promise<WorkflowActionCatalogEntry | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry>
        >(
          `${interpolateRoute(API_ROUTES.ADMIN.ACTIONS.GET, { id })}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? null
      },
      { on404: 'ignore', errorContext: 'Error fetching catalog action' },
    )
  }

  async getActionUsageForCompanyByAdmin(
    id: string,
    companyId: string,
  ): Promise<WorkflowActionUsage | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<WorkflowActionUsage>>(
          `${interpolateRoute(API_ROUTES.ADMIN.ACTIONS.GET_USAGE, { id })}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? null
      },
      { on404: 'ignore', errorContext: 'Error fetching catalog action usage' },
    )
  }

  async createActionForCompany(
    companyId: string,
    dto: SaveWorkflowActionDto,
  ): Promise<WorkflowActionCatalogEntry | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry>
        >(
          `${API_ROUTES.ADMIN.ACTIONS.CREATE}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error creating catalog action' },
    )
  }

  async updateActionForCompany(
    id: string,
    companyId: string,
    dto: SaveWorkflowActionDto,
  ): Promise<WorkflowActionCatalogEntry | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry>
        >(
          `${interpolateRoute(API_ROUTES.ADMIN.ACTIONS.UPDATE, { id })}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'PUT', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error updating catalog action' },
    )
  }

  async bulkSaveForCompany(
    companyId: string,
    payload: { existingId?: string; actions: SaveWorkflowActionDto[] },
  ): Promise<WorkflowActionCatalogEntry[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowActionCatalogEntry[]>
        >(
          `${API_ROUTES.ADMIN.ACTIONS.BULK_SAVE}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'POST', body: JSON.stringify(payload) },
        )
        return response.data ?? []
      },
      { errorContext: 'Error bulk-saving catalog actions' },
    )
    return result ?? []
  }

  async deleteActionForCompany(
    id: string,
    companyId: string,
  ): Promise<DeleteCatalogActionResult> {
    try {
      await this.request(
        `${interpolateRoute(API_ROUTES.ADMIN.ACTIONS.DELETE, { id })}?companyId=${encodeURIComponent(companyId)}`,
        { method: 'DELETE' },
      )
      return { ok: true }
    } catch (error) {
      return parseDeleteError(error)
    }
  }
}

function parseDeleteError(_error: unknown): DeleteCatalogActionResult {
  return { ok: false, code: 'error' }
}

export const actionsCatalogApiClient = new ActionsCatalogApiClient()
